export const MAX_UPLOAD_BYTES = 5 * 1024 * 1024;
const MAX_SOURCE_BYTES = 25 * 1024 * 1024;

// Keep small originals intact. Large item photos are resized locally before upload.
export async function prepareItemPhoto(file) {
  if (file.size <= MAX_UPLOAD_BYTES) return file;
  if (file.size > MAX_SOURCE_BYTES) {
    throw new Error('This photo is over 25 MB. Choose a smaller photo.');
  }

  const sourceUrl = URL.createObjectURL(file);
  try {
    const image = await new Promise((resolve, reject) => {
      const picture = new Image();
      picture.onload = () => resolve(picture);
      picture.onerror = () => reject(new Error('This photo could not be opened. Save it as JPG, PNG or WebP and try again.'));
      picture.src = sourceUrl;
    });
    if (!image.naturalWidth || !image.naturalHeight) {
      throw new Error('This photo could not be opened. Choose another photo.');
    }
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (!context) throw new Error('Your browser could not prepare this photo. Try a smaller JPG.');

    for (const longestSide of [2400, 1800, 1350]) {
      const scale = Math.min(1, longestSide / Math.max(image.naturalWidth, image.naturalHeight));
      canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
      canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));
      // JPEG needs a background for transparent PNGs.
      context.fillStyle = '#ffffff';
      context.fillRect(0, 0, canvas.width, canvas.height);
      context.drawImage(image, 0, 0, canvas.width, canvas.height);
      const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', 0.88));
      if (blob && blob.size <= MAX_UPLOAD_BYTES) {
        const name = file.name.replace(/\.[^.]+$/, '') || 'item-photo';
        return new File([blob], `${name}.jpg`, { type: 'image/jpeg', lastModified: file.lastModified });
      }
    }
    throw new Error('This photo could not be reduced below 5 MB. Choose a smaller photo.');
  } finally {
    URL.revokeObjectURL(sourceUrl);
  }
}
